import { PriceEditForm } from "./PriceEditForm";
import { ListingEditor } from "./ListingEditor";
import { useEffect, useRef, useState } from "react";
import { InStockTable } from "./InStockTable";
import {
  updateManualProduct,
  getReadinessDetail,
  type ReadinessDetail,
  listSheet,
  searchProducts,
  type ProductSourceFilter,
  type SearchRow,
  type SheetRow,
  type SheetSort,
  type SheetSortKey,
} from "./actions";

// The sheet: one row per product, with the feed data visible so gaps are obvious.
// READ-ONLY. Every tile is a filter, so clicking it opens exactly the rows it counts
// and the number can be checked by eye (ops/CORPUS_SCHEMA_CONTRACT.md section 7).
// Paging and sorting run on the SERVER, so they cover every product, not just the rows on screen. SEARCH is one box over
// SKU, part number, GTIN and manufacturer: case, dashes and spaces do not matter, results are ranked (exact first) and
// limited to the best 100. It is a prefix search (no "contains", no typo tolerance: that is the planned Typesense index).
// With one feed selected, sorts and search need Firestore indexes (ops/create_sheet_indexes.sh, create_search_indexes.sh);
// until they are built the panel says so instead of failing.

type F = Record<string, unknown>;
const str = (f: F, k: string) => (typeof f[k] === "string" ? (f[k] as string) : "");
const num = (f: F, k: string) => (typeof f[k] === "number" ? (f[k] as number) : null);
const arr = (f: F, k: string) => (Array.isArray(f[k]) ? (f[k] as unknown[]) : []);

function money(n: number | null) {
  return n == null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

const SOURCES: { key: ProductSourceFilter; label: string }[] = [
  { key: "", label: "All feeds" },
  { key: "ndc", label: "NDC" },
  { key: "dnh", label: "D&H" },
  { key: "kroll", label: "Kroll" },
  { key: "nms", label: "NMS brands" },
  { key: "ors", label: "ORS (inactive)" },
  { key: "msc", label: "MSC (inactive)" },
  { key: "shop", label: "Shop only (reconcile)" },
  { key: "excluded", label: "Excluded (in stock)" },
];

// WooCommerce product fields this corpus can answer today. Weight and dimensions are stored by every feed parser (NMS since 2026-10-30), so an empty
// one means the vendor did not send it. "notCaptured" stays in the type for any future column a parser does not read yet.
type Check = { label: string; state: "ok" | "missing" | "notCaptured"; value?: string };
function checks(f: F): Check[] {
  const has = (v: boolean, label: string, value?: string): Check =>
    ({ label, state: v ? "ok" : "missing", value });
  return [
    has(!!str(f, "name"), "Title", str(f, "name")),
    has(!!str(f, "description"), "Description", str(f, "description").slice(0, 80)),
    has(!!str(f, "sku"), "SKU", str(f, "sku")),
    has((num(f, "price") ?? 0) > 0, "Price", money(num(f, "price"))),
    has(arr(f, "images").length > 0, "Images", `${arr(f, "images").length}`),
    has(!!str(f, "manufacturer_name"), "Brand", str(f, "manufacturer_name")),
    has(!!str(f, "manufacturer_item_code"), "MPN", str(f, "manufacturer_item_code")),
    has(!!str(f, "gtin"), "GTIN", str(f, "gtin")),
    has(!!str(f, "category"), "Category", str(f, "category")),
    has(num(f, "quantity_on_hand") != null, "Stock quantity", String(num(f, "quantity_on_hand"))),
    has((num(f, "weight") ?? 0) > 0, "Weight", `${num(f, "weight")} ${str(f, "weight_uom")}`.trim()),
    has([num(f, "length"), num(f, "width"), num(f, "height")].every((x) => (x ?? 0) > 0), "Dimensions",
        `${num(f, "length")} × ${num(f, "width")} × ${num(f, "height")} ${str(f, "dim_uom")}`.trim()),
  ];
}
const completeness = (f: F) => {
  const c = checks(f).filter((x) => x.state !== "notCaptured");
  return Math.round((100 * c.filter((x) => x.state === "ok").length) / c.length);
};


type DetailTab = "preview" | "wc" | "gmc" | "all";
const DETAIL_TABS: { key: DetailTab; label: string }[] = [{ key: "preview", label: "Preview" }, { key: "wc", label: "WooCommerce" }, { key: "gmc", label: "Merchant Center" }, { key: "all", label: "All fields" }];

// A status dot: green only for "ready". A parent product ("container": it is never sold or listed itself) and a row whose status is not known get the hollow dot,
// never the green one -- they used to fall through to green, which made a priceless container look approved.
const statusDot = (st?: string) => (st === "blocked" ? "red" : st === "review" ? "orange" : st === "ready" ? "ok" : "none");

const RESULT_ICON: Record<string, string> = { pass: "✓", fail: "✗", warn: "!", pending: "…" };
const STATUS_TEXT: Record<string, string> = { container: "Container (not sold itself)", ready: "Ready", review: "Review (nothing blocks)", blocked: "Blocked" };

function Checklist({ title, status, checks }: { title: string; status: string; checks: ReadinessDetail["wc"]["checks"] }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
        <span className={`dot dot-${statusDot(status)}`} />{title}: {STATUS_TEXT[status] ?? status}
      </div>
      <table className="manual-products"><tbody>
        {checks.map((c) => (
          <tr key={c.id}>
            <td style={{ width: 24 }} title={c.severity}>{RESULT_ICON[c.result]}</td>
            <td>{c.label}</td>
            <td className="meta" style={{ margin: 0 }}>{c.detail}</td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}

// The two approvals, computed live from the stored product, the current pricing policy and the current exclusion rules.
function Approvals({ docId, tab }: { docId: string; tab: DetailTab }) {
  const [d, setD] = useState<ReadinessDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setD(null); setErr(null);
    getReadinessDetail(docId).then((r) => (r.ok ? setD(r.data) : setErr(r.error)));
  }, [docId]);
  if (err) return <div className="panel error">{err}</div>;
  if (!d) return <div className="meta">Checking the two approvals…</div>;
  return (
    <div style={{ marginTop: 8 }}>
      {tab === "wc" && <Checklist title="WooCommerce" status={d.wc.status} checks={d.wc.checks} />}
      {tab === "gmc" && <Checklist title="Merchant Center" status={d.gmc.status} checks={d.gmc.checks} />}
    </div>
  );
}

// NMS-brand rows are typed by people (no vendor feed), so their manufacturer, price and stock can be edited right in the table. The value saves when the cell is
// left, to the listing layer (the same place the approvals read), and the row is patched at once; a refresh recomputes the approvals.
function InlineCell({ value, kind, onCommit, className }: { value: string; kind: "text" | "number"; onCommit: (v: string) => Promise<string | null>; className?: string }) {
  const [v, setV] = useState(value);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setV(value), [value]);
  return <input className={className} type={kind} step={kind === "number" ? "0.01" : undefined} value={v} title={err ?? "Edit and click away to save"} aria-invalid={!!err}
    style={{ width: kind === "number" ? 84 : 150, ...(err ? { borderColor: "var(--bad)" } : {}) }} onClick={(e) => e.stopPropagation()}
    onChange={(e) => setV(e.target.value)}
    onBlur={async () => { if (v === value) return; setErr(await onCommit(v)); }} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setV(value); (e.target as HTMLInputElement).blur(); } }} />;
}

export function Preview({ row, onClose }: { row: SheetRow; onClose: () => void }) {
  const f = row.fields;
  const [proposing, setProposing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [version, setVersion] = useState(0);
  const [tab, setTab] = useState<DetailTab>("preview");
  const images = arr(f, "images").filter((x): x is string => typeof x === "string");
  return (
    <aside className="drawer" aria-label="Product preview">
      <div className="drawer-head">
        <div className="drawer-actions">
          <button onClick={() => setEditing((e) => !e)}>{editing ? "Hide editor" : "Edit listing"}</button>
          <button onClick={() => setProposing(true)}>Propose price edit</button>
          <button onClick={onClose} aria-label="Close the detail view">Close ✕</button>
        </div>
        <div className="drawer-title" title={`${str(f, "source_id")} · ${row.docId}`}>{str(f, "name") || "(no title)"}</div>
      </div>
      {proposing && (
        <PriceEditForm product={{ docId: row.docId, sku: str(f, "sku"), price: num(f, "price") }}
                       onClose={() => setProposing(false)} onProposed={() => {}} />
      )}

      {row.issue && (
        <div className={`panel ${row.issue.severity === "red" ? "error" : ""}`} style={{ marginTop: 8 }}>
          <strong>{row.issue.severity === "red" ? "Critical problem" : "Soft problem"} found in the feed</strong>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{row.issue.reasons.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}

      {editing && <ListingEditor docId={row.docId} onSaved={() => setVersion((n) => n + 1)} />}
      <div className="seg" role="tablist" style={{ marginTop: 12 }}>
        {DETAIL_TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {tab === "preview" && <div className="product-preview">
        <div className="preview-image">
          {images[0] ? <img src={images[0]} alt="" /> : <span className="meta" style={{ margin: 0 }}>No image in the feed</span>}
        </div>
        <div style={{ fontWeight: 600, marginTop: 8 }}>{str(f, "name") || "(no title)"}</div>
        <div style={{ margin: "4px 0" }}><b>Price:</b> {money(row.derived?.sellPrice ?? null)} <span className="meta" style={{ margin: 0 }}>(selling) · feed {money(num(f, "price"))}</span></div>
        <div className="meta" style={{ margin: "4px 0" }}><b>Brand:</b> {str(f, "manufacturer_name") || "none"} · <b>MPN:</b> {str(f, "manufacturer_item_code") || "none"} · <b>SKU:</b> {str(f, "sku")}</div>
        <p style={{ margin: "8px 0" }}>{str(f, "description") || <span className="meta">No description</span>}</p>
        <div className="meta" style={{ margin: "4px 0" }}><b>Category:</b> {str(f, "category") || "none"}</div>
        <div className="meta" style={{ margin: "4px 0" }}><b>Tags:</b> {(arr(f, "tags").length ? arr(f, "tags").join(", ") : str(f, "tags")) || "none"}</div>
      </div>}
      {(tab === "wc" || tab === "gmc") && <Approvals key={`${row.docId}-${version}`} docId={row.docId} tab={tab} />}

      {tab === "all" && <>
      <h3 style={{ margin: "16px 0 4px" }}>All fields</h3>
      <table className="manual-products">
        <tbody>
          {Object.entries(f).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
            <tr key={k}>
              <td className="mono">{k}</td>
              <td className="mono" style={{ wordBreak: "break-all" }}>
                {typeof v === "string" ? v.slice(0, 160) : JSON.stringify(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </>}
    </aside>
  );
}


const PAGE_SIZES = [25, 50, 100];
const MIN_QUERY = 3;
const alnum = (t: string) => t.replace(/[^a-z0-9]/gi, "");

export function SheetPanel({ initialView }: { initialView?: "policy" | "versions" } = {}) {
  const [source, setSource] = useState<ProductSourceFilter>("");
  const [filter, setFilter] = useState("d_in_stock");          // the default view is the working set: products in stock
  const [rows, setRows] = useState<SheetRow[]>([]);
  const patchField = async (r: SheetRow, field: "manufacturerName" | "price" | "quantityOnHand", key: string, raw: string): Promise<string | null> => {
    const num = field === "manufacturerName" ? raw : raw === "" ? null : Number(raw);
    const res = await updateManualProduct(r.docId, { [field]: num } as never);
    if (!res.ok) return res.error;
    const upd = (x: SheetRow) => (x.docId === r.docId ? { ...x, fields: { ...x.fields, [key]: field === "manufacturerName" ? raw : num }, typed: [...(x.typed ?? []), key] } : x);
    setRows((p) => p.map(upd)); setResults((p) => p.map((x) => (x.docId === r.docId ? upd(x as SheetRow) as typeof x : x)));
    return null;
  };
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SheetRow | null>(null);
  const [sort, setSort] = useState<SheetSort | null>(null);
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");                       // the search text actually sent (debounced)
  const [results, setResults] = useState<SearchRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const cursors = useRef<unknown[]>([undefined]);       // cursors[i] opens page i; page 0 has none
  const request = useRef(0);                            // ignores answers that arrive after a newer request
  const searching = alnum(q).length >= MIN_QUERY;

  useEffect(() => {
    const t = setTimeout(() => setQ(searchText.trim()), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  // Browsing: one server page at a time.
  const loadPage = async (n: number) => {
    const id = ++request.current;
    setLoading(true);
    const r = await listSheet(source, filter, { cursor: cursors.current[n], limit: pageSize, sort });
    if (id !== request.current) return;
    setLoading(false);
    if (!r.ok) { setError(r.error); setRows([]); setHasNext(false); return; }
    setError(null);
    setRows(r.data.rows);
    cursors.current[n + 1] = r.data.nextCursor ?? undefined;
    cursors.current.length = n + 2;
    setHasNext(!!r.data.nextCursor);
    setPage(n);
  };

  // Searching: the best 100 matches come back ranked in one answer; paging is within them.
  const runSearch = async () => {
    const id = ++request.current;
    setLoading(true);
    const r = await searchProducts(source, q);
    if (id !== request.current) return;
    setLoading(false);
    if (!r.ok) { setError(r.error); setResults([]); return; }
    setError(null);
    setResults(r.data.rows);
    setTruncated(r.data.truncated);
    setPage(0);
  };

  // The in-stock view runs on the snapshot in the browser: no server page, nothing to load here.
  const inStockMode = filter === "d_in_stock" && !searching;
  const feedSelect = (
    <label className="sheet-filter"><span>Feed</span>
      <select value={source} aria-label="Feed" onChange={(e) => { setFilter(filter === "" ? "" : "d_in_stock"); setSource(e.target.value as ProductSourceFilter); }}>
        {SOURCES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
      </select>
    </label>);
  const showSelect = (
    <label className="sheet-filter"><span>Show</span>
      <select value={filter === "d_in_stock" ? "stock" : "all"} disabled={source === "excluded"} aria-label="Which products"
              onChange={(e) => { setSearchText(""); setQ(""); setFilter(e.target.value === "stock" ? "d_in_stock" : ""); }}>
        <option value="stock">In stock</option><option value="all">All products, including no stock</option>
      </select>
    </label>);

  // Any change to what is being asked for starts again from page 1.
  const criteria = JSON.stringify([source, filter, pageSize, sort, searching ? q : null]);
  useEffect(() => {
    setSelected(null);
    cursors.current = [undefined];
    setPage(0);
    if (inStockMode) return;
    if (searching) runSearch(); else loadPage(0);
  }, [criteria]);

  // Searching looks at every product, so it clears the tile filter; picking a tile clears the search.
  useEffect(() => { if (searching && filter) setFilter(""); }, [searching]);

  const goto = (n: number) => (searching ? setPage(n) : loadPage(n));
  const shown: (SheetRow & { matchedOn?: string; exact?: boolean })[] =
    searching ? results.slice(page * pageSize, (page + 1) * pageSize) : rows;
  const next = searching ? (page + 1) * pageSize < results.length : hasNext;

  const headerClick = (key: SheetSortKey) =>
    setSort((s) => (s?.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null));

  const sortLocked = filter ? "Clear the tile filter to sort" : searching ? "Search results are ranked by best match" : "";
  const th = (label: string, key?: SheetSortKey) => {
    if (!key) return <th>{label}</th>;
    const on = sort?.key === key;
    return (
      <th className={`sortable${sortLocked ? " locked" : ""}`} title={sortLocked || "Sort all rows"}
          aria-sort={on ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}
          onClick={() => !sortLocked && headerClick(key)}>
        {label}<span className="sort-mark">{on ? (sort!.dir === "asc" ? " ▲" : " ▼") : ""}</span>
      </th>
    );
  };

  const total = searching ? results.length : null;
  const from = shown.length ? page * pageSize + 1 : 0;
  const to = page * pageSize + shown.length;
  const tooShort = searchText.trim() !== "" && alnum(searchText).length < MIN_QUERY;

  // Keyboard: Escape closes the detail, Up/Down move to the neighbouring row, so a list can be worked through without touching the mouse.
  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.key === "Escape") { setSelected(null); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const i = shown.findIndex((r) => r.docId === selected.docId);
        const n = shown[i + (e.key === "ArrowDown" ? 1 : -1)];
        if (n) { e.preventDefault(); setSelected(n); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, shown]);

  return (
    <div className={`sheet-page sheet-split${selected ? " has-detail" : ""}`}>
      <div className="sheet-main">
      <div className="sheet-top">
        {source === "shop" && (
          <div className="sheet-title">
            <span className="meta" style={{ color: "var(--warn)" }}>
              Products the shop carried that no vendor feed provides. They stay in the console until reconciled; only one with a managed quantity above 0 that passes WooCommerce and Merchant Center is restored to production. Unverified.
            </span>
          </div>
        )}
        {(source === "ors" || source === "msc") && (
          <div className="sheet-title">
            <span className="meta" style={{ color: "var(--warn)" }}>
              {source === "ors" ? "ORS: last-known data from the 2022-11-01 price file; no live feed, no stock quantity." : "MSC: last-known data from the 2024-10-01 price files (A and B stocked, plus direct-ship items a production store carried)."}
              {" "}Unverified. A row's store_* fields name the production product and its media-library images. Approvals shown are live estimates only.
            </span>
          </div>
        )}


        <form className="sheet-controls" hidden={inStockMode} onSubmit={(e) => { e.preventDefault(); setQ(searchText.trim()); }}>
          <div className="searchbox">
            <input type="search" className="sheet-search" value={searchText}
                   placeholder="Search SKU, part number, GTIN or manufacturer…"
                   title="Case, dashes and spaces do not matter. Matches the start of a SKU, part number, GTIN or manufacturer name; best 100, exact match first."
                   onChange={(e) => setSearchText(e.target.value)} aria-label="Search products" />
            <button type="submit">Search</button>
          </div>
          {feedSelect}
          {showSelect}
          {(searchText || sort) && <button type="button" onClick={() => { setSearchText(""); setQ(""); setSort(null); }}>Clear</button>}
          {tooShort && <span className="meta sheet-status">Type at least {MIN_QUERY} letters or digits</span>}
        </form>

        {error && <div className="panel error sheet-error">{error}</div>}
      </div>

      {inStockMode && <InStockTable initialView={initialView} source={source} onSource={(x) => { setFilter("d_in_stock"); setSource(x); }} selectedId={selected?.docId} onSelect={(r) => setSelected(r)} extra={<>{feedSelect}{showSelect}</>} />}

      <div className="sheet-card" hidden={inStockMode}>
      <div className="sheet-scroll">
        <table className="manual-products sheet-table">
          <thead>
            <tr>
              <th title="Green: the feed guard found nothing wrong. Orange: a soft problem. Red: a critical problem. Hover a dot for the reasons.">Status</th>
              <th title="WooCommerce approval: green ready, orange review (nothing blocks), red blocked. Hover for what blocks.">Woo</th>
              <th title="Merchant Center approval: green ready, orange review, red blocked. Hover for what blocks.">GMC</th>
              <th title="The console's selling price (cost-plus, MAP floor). The vendor's list price is never used.">Sell price</th>
              <th>Complete</th>{searching && <th>Match</th>}{th("Feed")}{th("SKU", "sku")}{th("Name", "name")}{th("Manufacturer", "manufacturer")}
              {th("MPN", "mpn")}{th("Category")}{th("Cost", "cost")}{th("Feed price", "price")}{th("MAP", "map")}
              {th("Qty", "qty")}{th("GTIN")}{th("Img")}
              <th title="The vendor file time at which this product's quantity, status, price or cost last changed">Stock updated</th>
              <th title="The vendor file time at which anything else about this product last changed">Catalog updated</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const f = r.fields;
              return (
                <tr key={r.docId} className={selected?.docId === r.docId ? "selected" : ""} onClick={() => setSelected(r)}>
                  <td><span className={`dot dot-${r.issue?.severity ?? "ok"}`} role="img"
                            aria-label={r.issue ? `${r.issue.severity}: ${r.issue.reasons.join("; ")}` : "no problem found"}
                            title={r.issue ? r.issue.reasons.join("\n") : "No problem found by the feed guard"} /></td>
                  <td><span className={`dot dot-${statusDot(r.derived?.wc)}`} role="img"
                            aria-label={`WooCommerce ${r.derived?.wc ?? "unknown"}`} title={r.derived ? `WooCommerce: ${r.derived.wc}${r.derived.wcBlocks.length ? "\n" + r.derived.wcBlocks.join("\n") : ""}` : ""} /></td>
                  <td><span className={`dot dot-${statusDot(r.derived?.gmc)}`} role="img"
                            aria-label={`Merchant Center ${r.derived?.gmc ?? "unknown"}`} title={r.derived ? `Merchant Center: ${r.derived.gmc}${r.derived.gmcBlocks.length ? "\n" + r.derived.gmcBlocks.join("\n") : ""}` : ""} /></td>
                  <td className="num" title={r.derived?.priceFlags.join(", ")}>{r.derived?.wc === "container" ? <span className="meta" style={{ margin: 0 }} title="Parent product: only the container for its variations, never sold or priced itself">—</span>
                    : r.derived?.sellPrice != null ? money(r.derived.sellPrice) : <span className="tile warn" style={{ padding: "1px 6px" }}>none</span>}</td>
                  <td className="num">{r.derived?.wc === "container" ? "—" : `${completeness(f)}%`}</td>
                  {searching && <td><span className={`match${r.exact ? " exact" : ""}`}>{r.matchedOn}{r.exact ? " ✓" : ""}</span></td>}
                  <td>{str(f, "source_id")}</td>
                  <td className="mono">{str(f, "sku")}</td>
                  <td><span className="clip clip-name" title={str(f, "name")}>{str(f, "name")}</span></td>
                  <td>{str(f, "source_id").startsWith("nms-")
                    ? <InlineCell kind="text" value={str(f, "manufacturer_name")} onCommit={(v) => patchField(r, "manufacturerName", "manufacturer_name", v)} />
                    : <span className="clip" title={str(f, "manufacturer_name")}>{str(f, "manufacturer_name")}</span>}</td>
                  <td className="mono">{str(f, "manufacturer_item_code") || "—"}</td>
                  <td><span className="clip" title={str(f, "category")}>{str(f, "category")}</span></td>
                  <td className="num">{money(num(f, "wholesale_cost"))}</td>
                  <td className="num">{str(f, "source_id").startsWith("nms-")
                    ? <InlineCell kind="number" value={num(f, "price") == null ? "" : String(num(f, "price"))} onCommit={(v) => patchField(r, "price", "price", v)} />
                    : money(num(f, "price"))}</td>
                  <td className="num">{num(f, "map_price") == null ? "—" : (num(f, "map_price") as number) === 0 ? "0 (none)" : money(num(f, "map_price"))}</td>
                  <td className="num">{str(f, "source_id").startsWith("nms-")
                    ? <InlineCell kind="number" value={num(f, "quantity_on_hand") == null ? "" : String(num(f, "quantity_on_hand"))} onCommit={(v) => patchField(r, "quantityOnHand", "quantity_on_hand", v)} />
                    : (num(f, "quantity_on_hand") ?? "—")}</td>
                  <td className="mono">{str(f, "gtin") || <span className="tile warn" style={{ padding: "1px 6px" }}>missing</span>}</td>
                  <td className="num">{arr(f, "images").length || <span className="tile warn" style={{ padding: "1px 6px" }}>0</span>}</td>
                  <td className="mono">{str(f, "stock_updated_at").slice(0, 16).replace("T", " ") || "—"}</td>
                  <td className="mono">{str(f, "catalog_updated_at").slice(0, 16).replace("T", " ") || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {shown.length === 0 && !loading && !error && (
          <p className="meta" style={{ padding: 16 }}>
            {searching ? "No product starts with that. Search matches the beginning of a SKU, part number, GTIN or manufacturer; it does not match the middle of a word or tolerate typos." : "No rows match."}
          </p>
        )}
      </div>

      <div className="sheet-pager" role="navigation" aria-label="Pagination">
        <span className="meta">
          {shown.length ? `Rows ${from.toLocaleString()}–${to.toLocaleString()}` : "No rows"}
          {total != null ? ` of ${total.toLocaleString()}${searching && truncated ? "+" : ""}` : ""} · Page {page + 1}
        </span>
        <span className="sheet-pager-buttons">
          <label>Rows per page{" "}
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="button" disabled={loading || page === 0} onClick={() => goto(0)}>First</button>
          <button type="button" disabled={loading || page === 0} onClick={() => goto(page - 1)}>Previous</button>
          <button type="button" disabled={loading || !next} onClick={() => goto(page + 1)}>Next</button>
        </span>
      </div>
      </div>

      </div>
      {selected && <Preview row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
